import React, { Component } from "react";
import {
  Grid,
  Icon,
  Image,
  Divider,
  Button,
  Message,
  Dropdown,
} from "semantic-ui-react";
import { PDFExport } from "@progress/kendo-react-pdf";

import DynamicAttendanceComponent from "../Attendance";
import { formatName } from "../../../utils/nameUtils";
import { departmentLabel } from "../subjectTemplate";
import { unapprovedSubjects } from "../../../utils/workflowStatus";

/**
 * The chrome every three-term report card shares: the PDF wrapper, letterhead,
 * heading, student block, attendance table, and the print button.
 *
 * The grade table itself is passed as children, because that is the only part
 * that differs between a numeric card and a descriptive one.
 *
 * CardLegacy4Q does not use this - it keeps its own copy so historical cards
 * cannot be affected by anything changed here.
 */

// Who may jump between learners from the card itself. Same list the legacy
// card gates its dropdown on.
const MAY_PICK_STUDENT = ["admin", "principal", "registrar", "adviser"];

class CardShell extends Component {
  exportPDFWithComponent = () => {
    this.pdfExportComponent.save();
  };

  /**
   * The "next learner" dropdown that sits above the card.
   *
   * The legacy card builds this itself; the three-term cards took a
   * `studentPicker` element as a prop instead - and nothing ever passed one, so
   * the dropdown simply disappeared for every SY 2026-2027 card. It belongs
   * here, beside the other chrome, built from the props the container already
   * sends down.
   */
  renderStudentPicker() {
    const { sectionStudents, sectionStudentId, handleChange, userType } = this.props;

    if (MAY_PICK_STUDENT.indexOf(userType) === -1) return null;
    if (!sectionStudents || sectionStudents.length === 0) return null;

    const options = sectionStudents
      .filter((s) => s && s.student)
      .map((s) => ({
        key: s.sectionStudentId,
        value: s.sectionStudentId,
        text: `${formatName(s.student.lastName)}, ${formatName(s.student.firstName)}`,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    return (
      <Grid.Row centered>
        <Dropdown
          selection
          search
          name="sectionStudentId"
          options={options}
          onChange={handleChange}
          value={sectionStudentId}
        />
      </Grid.Row>
    );
  }

  render() {
    const {
      sectionStudent,
      schoolYear,
      adviser,
      attendance,
      daysOfSchool,
      children,
      sectionSubjects,
      currentQuarter,
      scheme,
    } = this.props;

    const student = sectionStudent.student;
    const section = sectionStudent.section;
    const pending = unapprovedSubjects(sectionSubjects, parseInt(currentQuarter, 10));

    return (
      <Grid>
        {this.renderStudentPicker()}

        {/* Screen only - everything here sits outside the PDFExport below. */}
        {pending.length > 0 && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message warning>
                <Message.Header>
                  {pending.length === 1
                    ? "One subject has not been approved yet"
                    : `${pending.length} subjects have not been approved yet`}
                </Message.Header>
                <p>
                  {pending.join(", ")}. The card still prints - this is a note, not a
                  block - but the marks above may change before they are signed off.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={16} textAlign="right">
            {/* The PACE form is the other half of a Grade 1 card packet. */}
            {scheme && scheme.usesPace && (
              <Button
                basic
                as="a"
                href={`/pace/print/${sectionStudent.sectionStudentId}`}
                icon
                labelPosition="left"
                title="Open the printable PACE form for this learner"
              >
                <Icon name="list alternate outline" />
                PACE Form
              </Button>
            )}
            <Button
              primary
              icon
              labelPosition="left"
              onClick={this.exportPDFWithComponent}
            >
              <Icon name="print" />
              Print
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Column width={16}>
          <PDFExport
            ref={(component) => (this.pdfExportComponent = component)}
            paperSize="auto"
            margin={40}
            scale={2}
            fileName={`Report Card for ${formatName(student.lastName)}, ${formatName(
              student.firstName
            )}`}
            author="SALIS"
          >
            <Grid style={{ fontSize: "16px", fontFamily: "Arial" }}>
              <Grid.Row centered>
                <Grid.Column width={10}>
                  <Image src="/salis_letter_head_2026.png" />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row centered style={{ margin: 0, padding: 0 }}>
                <Grid.Column width={15} style={{ margin: 0, padding: 0 }}>
                  <Divider />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row centered style={{ margin: 0, padding: 0 }}>
                <Grid.Column
                  width={16}
                  style={{ margin: 0, padding: 0, textAlign: "center" }}
                >
                  {/* Wording from the registrar's SY 2026-2027 form. */}
                  <h1 style={{ marginBottom: 0, fontFamily: "Arial" }}>
                    LEARNER'S PERFORMANCE REPORT
                  </h1>
                  <h3
                    style={{ marginTop: 0, marginBottom: 0, fontFamily: "Arial" }}
                  >
                    {departmentLabel(section.levelType)}
                  </h3>
                  <h3 style={{ marginTop: 0, fontFamily: "Arial" }}>
                    {`SY ${schoolYear.name}`}
                  </h3>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <p>
                    Name: {formatName(student.lastName)},{" "}
                    {formatName(student.firstName)}{" "}
                    {formatName(student.middleName)}
                    <br />
                    Section: {section.gradeLevel} - {section.name}
                  </p>
                </Grid.Column>
                <Grid.Column width={8} textAlign="right">
                  <p>
                    LRN: {student.lrn} <br />
                    Adviser: {Boolean(adviser) && formatName(adviser.firstName)}{" "}
                    {Boolean(adviser) && formatName(adviser.lastName)}
                  </p>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            <Grid centered style={{ fontSize: "16px", fontFamily: "Arial" }}>
              {children}
            </Grid>

            <DynamicAttendanceComponent
              attendance={attendance}
              daysOfSchool={daysOfSchool}
              schoolYear={schoolYear}
            />
          </PDFExport>
        </Grid.Column>
      </Grid>
    );
  }
}

export default CardShell;
